import "./css/style.scss";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import {
  CampaignType,
  ICONS_DEFAULT_VALUES,
  LayoutDevices,
  LayoutDeviceSettings,
  Offer,
  PlacementStage,
  QueryParams,
  QuerySaveEmail,
  SelectedOfferStorage,
  TemplateIcons,
  TemplateOptionSettings,
  normalizeSectionSettings,
} from "../../../types/global.types";
import { getTrackingEvent, postTagEmailOffer } from "../../../services/offers/offers.service";
import TemplateSingle from "../single/TemplateSingle.component";
import LayoutEmail from "../../layout/email";
import PreCountAndSelectAll from "../components/PreCountAndSelectAll/PreCountAndSelectAll";
import TemplateFilters from "../components/Filters/desktop/TemplateFilters";
import TemplateIcon from "../components/Icons/info.component";
import FooterText from "../components/FooterText/FooterText";
import EmailAutomaticTermsBanner from "../components/EmailAutomaticTermsBanner/EmailAutomaticTermsBanner.component";
import { getHeaderEmailAutomaticTermsText } from "../utils/emailAutomaticTerms.utils";
import { EMAIL_REGEX } from "../../../utility/regexp";
import { getStorageItem, updateStorageItem } from "../../../utility/storage";
import PreviewSections from "./sections/PreviewSections.component";
import PreviewTopMenu from "./topMenu/PreviewTopMenu.component";

const DEFAULT_COOKIE_OPTIONS = {
  allowCookies: false,
  allowSessionCookies: false,
  allowPersistentCookies: false,
  expiryDays: "1000",
};

const applyPersistedSelection = (
  offers: Offer[],
  selected: string[] = []
): Offer[] => {
  const selectedIds = new Set((selected || []).map((id) => String(id)));
  return offers.map((offer) => ({
    ...offer,
    isAdded: selectedIds.has(String(offer.id)),
  }));
};

interface Props {
  params: QueryParams;
  layoutSettings: LayoutDeviceSettings;
  campaignId: string;
  placementId: string;
  placementName: string;
  campaignRes: CampaignType;
  selectedOffers: string[];
  singleOffer: Offer | undefined;
  isEmail: boolean;
  isSave: boolean | undefined;
  isShare: boolean | undefined;
  email: string | undefined;
  slot: number;
  sessionId: string;
  isSeen: boolean;
  setSingle: React.Dispatch<React.SetStateAction<Offer | undefined>>;
  setIsEmail: React.Dispatch<React.SetStateAction<boolean>>;
  layoutId: string;
  device: LayoutDevices;
}

const PreviewDynamic = ({
  params,
  layoutSettings,
  isSave,
  isShare,
  campaignRes,
  campaignId,
  placementId,
  placementName,
  selectedOffers,
  singleOffer,
  setSingle,
  setIsEmail,
  isEmail,
  email,
  slot,
  isSeen,
  sessionId,
  layoutId,
  device,
}: Props) => {
  const settingsTemplate = layoutSettings?.template;
  const settings: TemplateOptionSettings | undefined = layoutSettings.settings;
  const [displayOnly] = useState<boolean>(
    campaignRes?.placementSettings[0]?.displayOnly
  );
  const [campaign, setCampaign] = useState<CampaignType | undefined>(() =>
    campaignRes
      ? {
        ...campaignRes,
        offers: applyPersistedSelection(campaignRes.offers, selectedOffers),
      }
      : campaignRes
  );
  const [preEmail, setPreEmail] = useState<string | undefined>(
    () =>
      email?.trim() ||
      getStorageItem(
        "brswEmail",
        campaignRes?.cookieOptions || DEFAULT_COOKIE_OPTIONS
      )?.trim() ||
      undefined
  );
  const [isSingleSave, setSingleSave] = useState<boolean>(false);
  const [hasOpen, setHasOpen] = useState<boolean>(false);
  const [offerFilteredList, setOfferFilteredList] = useState<Offer[]>(() =>
    applyPersistedSelection(campaignRes.offers, selectedOffers)
  );
  const [autoSendingOfferId, setAutoSendingOfferId] = useState<string>("");

  const updateCampaignBySingle = (o: Offer) => {
    if (campaign) {
      const newOffers: Offer[] = campaign.offers.map((offer: Offer) => {
        if (offer.id === o.id) {
          return o;
        }
        return offer;
      });

      setCampaign(
        (prevState) => prevState && { ...prevState, offers: newOffers }
      );
      setOfferFilteredList(newOffers);
    }
  };

  const updateFilteredOffers = (newOffers: Offer[], currentOffers: Offer[]) => {
    const currentOfferIds = new Set(currentOffers.map((i) => String(i.id)));
    setOfferFilteredList(
      newOffers.filter((f) => currentOfferIds.has(String(f.id)))
    );
  };

  const handleFilteredListChange: Dispatch<SetStateAction<Offer[]>> = (
    value
  ) => {
    setOfferFilteredList((prev) => {
      const next = typeof value === "function" ? value(prev) : value;
      return applyPersistedSelection(next, selectedOffers);
    });
  };

  const persistDynamicEmail = (value: string) => {
    const normalized = (value || "").trim();
    if (!normalized) {
      return;
    }

    const existing = email?.trim() || preEmail?.trim() || "";
    if (existing) {
      if (!preEmail?.trim()) {
        setPreEmail(existing);
      }
      return;
    }

    const cookieOptions = campaign?.cookieOptions || DEFAULT_COOKIE_OPTIONS;
    updateStorageItem("brswEmail", normalized, cookieOptions);
    window.top?.postMessage({ event: "setPreEmail", load: normalized }, "*");
    setPreEmail(normalized);
  };

  useEffect(() => {
    window.addEventListener("message", function (event) {
      if (Object.keys(event.data).includes("single")) {
        updateCampaignBySingle(event.data.single);
      }
    });

    return () => {
      window.removeEventListener("message", () => { });
    };
    // eslint-disable-next-line
  }, [campaign]);

  const resolvedEmailForAutoSend = useMemo(() => {
    if (preEmail?.trim()) {
      return preEmail.trim();
    }
    if (email?.trim()) {
      return email.trim();
    }

    const cookieOptions = campaign?.cookieOptions || {
      allowCookies: false,
      allowSessionCookies: false,
      allowPersistentCookies: false,
      expiryDays: "1000",
    };
    return getStorageItem("brswEmail", cookieOptions)?.trim() || "";
  }, [campaign?.cookieOptions, email, preEmail]);

  const canAutoSendEmailFromLabel =
    campaign?.campaignGeneralOptions?.manualEmailEntry === false &&
    Boolean(resolvedEmailForAutoSend) &&
    EMAIL_REGEX.test(resolvedEmailForAutoSend);

  const canAutoSendEmailFromMainCTA =
    campaign?.campaignGeneralOptions?.autoSendEmail === true &&
    campaign?.campaignGeneralOptions?.manualEmailEntry === false &&
    Boolean(resolvedEmailForAutoSend) &&
    EMAIL_REGEX.test(resolvedEmailForAutoSend);

  const handleAutoSendFromOfferLabel = async (offer: Offer, slot: number) => {
    if (
      offer.offerSettings?.bundleOptions?.isBundled ||
      !resolvedEmailForAutoSend.trim()
    ) {
      return;
    }

    setAutoSendingOfferId(offer.id);
    const emailParams: QuerySaveEmail = {
      offerid: offer.id,
      email: resolvedEmailForAutoSend,
      offerLink: offer.trackingUrl,
      companyId: campaign?.company_Id || "",
      placementId,
      campaignId,
      templateId: settingsTemplate.identifier,
      slot,
      orderId: offer.orderId,
      pltype: displayOnly ? PlacementStage.pre : PlacementStage.post,
      layoutId,
    };

    try {
      const response = await postTagEmailOffer(emailParams);
      getTrackingEvent({
        event: "onsendemailclick",
        retailerId: campaign?.company_Id,
        campaignId,
        offerId: offer.id,
        placementId,
        placementName,
        tid: settingsTemplate.identifier,
        slot,
        pltype: displayOnly ? PlacementStage.pre : PlacementStage.post,
        pltid: layoutId,
      });

      if (response?.data?.status) {
        updateStorageItem(
          "brswEmail",
          resolvedEmailForAutoSend,
          campaign?.cookieOptions
        );
        setPreEmail(resolvedEmailForAutoSend);
      }
    } finally {
      setAutoSendingOfferId("");
    }
  };

  const handleCloseSingle = () => {
    setSingle(undefined);
    setSingleSave(false);
  };

  const checkSelectedOffers = (selected: string[] = []) => {
    setCampaign(
      (prevState) =>
        prevState && {
          ...prevState,
          offers: applyPersistedSelection(prevState.offers, selected),
        }
    );
    setOfferFilteredList((prev) => applyPersistedSelection(prev, selected));
  };

  const openSingle = (
    offer: Offer,
    isSave: boolean,
    isAutoEmail: boolean,
    slot: number,
    isShare?: boolean
  ) => {
    if (isAutoEmail) {
      const params = { offer, isSave, isAuto: true, slot };
      window.top?.postMessage(
        { event: "emailPopupEmbedded", load: params },
        "*"
      );
    } else {
      window.parent.postMessage(
        {
          event: "openSingleEmbedded",
          load: { offer, isSave, slot, isShare: isShare || false },
        },
        "*"
      );
    }
  };

  const handleAdd = (offer: Offer, value: boolean, slot: number) => {
    setSelectedOfferStorage({
      id: offer.id,
      link: offer.trackingUrl,
      active: value,
      layoutId: layoutId || "",
    });
    if (campaign) {
      const newOffers: Offer[] = campaign.offers.map((_offer: Offer) => {
        if (String(_offer.id) === String(offer.id)) {
          return { ..._offer, isAdded: value };
        }
        return _offer;
      });
      setCampaign(
        (prevState) => prevState && { ...prevState, offers: newOffers }
      );
      updateFilteredOffers(newOffers, offerFilteredList);
    } else {
      const _single = { ...singleOffer, isAdded: value };
      setSingle((prevState) => prevState && { ...prevState, isAdded: value });
      window.top?.postMessage(
        { event: "singleChange", offer: _single, slot },
        "*"
      );
    }

    value &&
      campaign &&
      getTrackingEvent({
        event: "onselectoffer",
        offerId: offer.id,
        retailerId: campaign.company_Id,
        campaignId: campaign.id,
        placementId,
        placementName,
        tid: settingsTemplate.identifier,
        slot,
        pltype: displayOnly ? PlacementStage.pre : PlacementStage.post,
        pltid: layoutId,
      });
  };

  const handleSelectAll = () => {
    if (!campaign) {
      return;
    }

    const selectedLength = offerFilteredList.filter(
      (o) =>
        selectedOffers.some((id) => String(id) === String(o.id)) &&
        !o.tagBlocked?.isBlocked
    ).length;
    const offerLength = offerFilteredList.filter(
      (o) => !o.tagBlocked?.isBlocked
    ).length;
    const shouldRemove = selectedLength === offerLength;

    // Batch update all offers at once instead of individual handleAdd calls
    const offersToUpdate = offerFilteredList.filter(
      (offer) =>
        !offer.tagBlocked?.isBlocked &&
        (shouldRemove ? offer.isAdded : !offer.isAdded)
    );

    if (offersToUpdate.length === 0) {
      return;
    }

    // Update campaign state with all changes at once
    const newOffers: Offer[] = campaign.offers.map((offer: Offer) => {
      const shouldUpdate = offersToUpdate.some(
        (o) => String(o.id) === String(offer.id)
      );
      if (shouldUpdate) {
        return { ...offer, isAdded: !shouldRemove };
      }
      return offer;
    });

    setCampaign(
      (prevState) => prevState && { ...prevState, offers: newOffers }
    );
    updateFilteredOffers(newOffers, offerFilteredList);

    // Batch send storage updates for all offers using setSelectedOffersStorage
    const storageUpdates: SelectedOfferStorage[] = offersToUpdate.map(
      (offer) => ({
        id: offer.id,
        link: offer.trackingUrl,
        active: !shouldRemove,
        layoutId: layoutId || "",
      })
    );

    // Send all storage updates at once using the new batch method
    setSelectedOffersStorage(storageUpdates);

    // Batch tracking events for selected offers (only when adding, not removing)
    if (!shouldRemove && campaign) {
      offersToUpdate.forEach((offer) => {
        getTrackingEvent({
          event: "onselectoffer",
          offerId: offer.id,
          retailerId: campaign.company_Id,
          campaignId: campaign.id,
          placementId,
          placementName,
          tid: settingsTemplate.identifier,
          slot: offer.slot,
          pltype: displayOnly ? PlacementStage.pre : PlacementStage.post,
          pltid: layoutId,
        });
      });
    }
  };

  const setSelectedOfferStorage = (storage: SelectedOfferStorage) => {
    window.top?.postMessage({ event: "setSelectedOffer", load: storage }, "*");
  };

  const setSelectedOffersStorage = (storage: SelectedOfferStorage[]) => {
    window.top?.postMessage({ event: "setSelectedOffers", load: storage }, "*");
  };

  useEffect(() => {
    if (campaignRes) {
      setCampaign({
        ...campaignRes,
        offers: applyPersistedSelection(campaignRes.offers, selectedOffers),
      });
      if (campaignRes && !singleOffer) {
        !hasOpen &&
          getTrackingEvent({
            event: "ontemplateopen",
            retailerId: campaignRes.company_Id,
            campaignId: campaignRes.id,
            placementId,
            placementName,
            tid: settingsTemplate.identifier,
            pltype: displayOnly ? PlacementStage.pre : PlacementStage.post,
            pltid: layoutId,
          });
      }
      setHasOpen(true);
    }
    // eslint-disable-next-line
  }, [campaignRes]);

  useEffect(() => {
    checkSelectedOffers(selectedOffers);
    // eslint-disable-next-line
  }, [selectedOffers]);

  useEffect(() => {
    if (email?.trim() && !preEmail?.trim()) {
      setPreEmail(email);
    }
  }, [email, preEmail]);

  useEffect(() => {
    document.body.classList.add("no-scroll");
  }, []);

  const svgAnimationKey = [
    settings?.mainLayout?.iconAnimation,
    settings?.mainLayout?.iconAnimationType,
    settings?.mainLayout?.iconAnimationRepeat,
  ].join("-");

  const svgAnimationStyle = settings?.mainLayout?.iconAnimation
    ? {
      animation: `${settings?.mainLayout?.iconAnimationType} 1s linear`,
      animationIterationCount: settings?.mainLayout?.iconAnimationRepeat,
      display: "inline-flex",
    }
    : {};

  const resolvedEmailForBanner =
    preEmail || email || params?.contactDetails?.[0]?.email;
  const headerEmailAutomaticTermsText = getHeaderEmailAutomaticTermsText(
    settings?.mainLayout,
    campaign?.campaignGeneralOptions,
    settingsTemplate.identifier
  );

  return (
    <>
      {singleOffer && (
        <>
          {isEmail ? (
            <LayoutEmail
              settings={layoutSettings.popup}
              templateSettings={layoutSettings.settings}
              emailPopupOffer={singleOffer}
              isEmbedded={true}
              setIsEmail={setIsEmail}
              setSingle={setSingle}
              prePopulateEmail={preEmail}
              campaignObj={campaignRes}
              campaignId={campaignRes.id}
              placementId={placementId}
              placementName={placementName}
              slot={slot}
              layoutId={layoutId}
            ></LayoutEmail>
          ) : (
            <>
              {campaign?.id && settings && (
                <TemplateSingle
                  slot={slot}
                  settings={settings}
                  isInline={true}
                  offer={singleOffer}
                  isSave={isSave || isSingleSave}
                  isShare={isShare || false}
                  placementId={placementId}
                  placementName={placementName}
                  settingsTemplate={settingsTemplate}
                  campaignObj={campaign}
                  displayOnly={displayOnly}
                  campaignId={campaign?.id}
                  companyId={campaign.company_Id}
                  updateCampaign={handleAdd}
                  setPreEmail={setPreEmail}
                  preEmail={preEmail || email}
                  closeSignle={handleCloseSingle}
                  layoutId={layoutId}
                />
              )}
            </>
          )}
        </>
      )}
      <div className="edynamic">
        <div
          className={`inline-wrapper ${device} ${settings?.mainLayout?.align} ${singleOffer || isEmail ? "hidden" : "displayed"
            }`}
          aria-label="Special offers and promotions"
          style={{
            width: settings?.embeddedElement?.fullWidth
              ? "100%"
              : settings?.embeddedElement?.width,
            padding: `0 ${settings?.embeddedElement?.margin || "0"}${settings?.embeddedElement?.marginType || "%"
              }`,
          }}
        >
          <div
            className={`inline-popup`}
            style={{
              padding: `${typeof settings?.mainLayout.paddingTopBottom !== "undefined"
                ? settings?.mainLayout.paddingTopBottom
                : 20
                }px ${typeof settings?.mainLayout.paddingLeftRight !== "undefined"
                  ? settings?.mainLayout.paddingLeftRight
                  : 35
                }px`,
              backgroundColor: settings?.mainLayout.backgroundColor,
              fontFamily: settings?.mainLayout.fontFamily,
              color: settings?.mainLayout.textColor,
              height: "auto",
            }}
          >
            <div
              style={{
                maxWidth: `${settings?.embeddedElement?.maxWidth || 100}${settings?.embeddedElement?.maxWidthType || "%"
                  }`,
                margin: "auto",
              }}
            >
              {settings?.mainLayout.headerHTML && (
                <div
                  className="inline-header-html"
                  dangerouslySetInnerHTML={{
                    __html: settings?.mainLayout.headerHTML,
                  }}
                ></div>
              )}
              <div className={`inline-title`}>
                {settings?.mainLayout?.headerIcon === "gift" && (
                  <>
                    <div
                      className="inline-title-icon"
                      key={svgAnimationKey}
                      style={svgAnimationStyle}
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M17.2 10V19H2.8V10M10 19V5.5M10 5.5H5.95C5.35326 5.5 4.78097 5.26295 4.35901 4.84099C3.93705 4.41903 3.7 3.84674 3.7 3.25C3.7 2.65326 3.93705 2.08097 4.35901 1.65901C4.78097 1.23705 5.35326 1 5.95 1C9.1 1 10 5.5 10 5.5ZM10 5.5H14.05C14.6467 5.5 15.219 5.26295 15.641 4.84099C16.0629 4.41903 16.3 3.84674 16.3 3.25C16.3 2.65326 16.0629 2.08097 15.641 1.65901C15.219 1.23705 14.6467 1 14.05 1C10.9 1 10 5.5 10 5.5ZM1 5.5H19V10H1V5.5Z"
                          stroke={settings?.mainLayout.headerTextColor}
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </>
                )}

                {settings?.mainLayout?.headerIcon === "lock" && (
                  <>
                    <div
                      className="inline-title-icon"
                      key={svgAnimationKey}
                      style={svgAnimationStyle}
                    >
                      {displayOnly ? (
                        <svg
                          width="42"
                          height="47"
                          viewBox="0 0 42 47"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M9.88889 21.25V12.25C9.88889 9.26631 11.0595 6.40483 13.1433 4.29505C15.227 2.18526 18.0532 1 21 1C23.9469 1 26.773 2.18526 28.8567 4.29505C30.9405 6.40483 32.1111 9.26631 32.1111 12.25V21.25M5.44444 21.25H36.5556C39.0102 21.25 41 23.2647 41 25.75V41.5C41 43.9853 39.0102 46 36.5556 46H5.44444C2.98985 46 1 43.9853 1 41.5V25.75C1 23.2647 2.98985 21.25 5.44444 21.25Z"
                            stroke={settings?.mainLayout.headerTextColor}
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      ) : (
                        <svg
                          width="42"
                          height="47"
                          viewBox="0 0 42 47"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M9.88889 21.2554V12.258C9.88613 9.46896 10.9073 6.77834 12.7542 4.7085C14.601 2.63865 17.1419 1.33726 19.8834 1.05697C22.6248 0.776667 25.3714 1.53746 27.5899 3.19164C29.8084 4.84583 31.3406 7.2754 31.8889 10.0087M5.44444 21.2572H36.5556C39.0102 21.2572 41 23.2714 41 25.7559V41.5013C41 43.9859 39.0102 46 36.5556 46H5.44444C2.98985 46 1 43.9859 1 41.5013V25.7559C1 23.2714 2.98985 21.2572 5.44444 21.2572Z"
                            stroke={settings?.mainLayout.headerTextColor}
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                  </>
                )}

                {settings?.mainLayout?.headerIcon === "custom" &&
                  settings?.mainLayout?.headerCustomIcon && (
                    <div
                      className="inline-title-icon"
                      key={svgAnimationKey}
                      style={svgAnimationStyle}
                      dangerouslySetInnerHTML={{
                        __html: settings.mainLayout.headerCustomIcon,
                      }}
                    />
                  )}

                <span
                  className={`inline-title-label`}
                  style={{
                    fontSize: settings?.mainLayout.headerFontSize + "px",
                    fontWeight: settings?.mainLayout.headerFontWeight,
                    color: settings?.mainLayout.headerTextColor,
                  }}
                  tabIndex={0}
                  role="contentinfo"
                  aria-label={settings?.mainLayout.headerText || ""}
                >
                  {settings?.mainLayout.headerText || ""}
                </span>
              </div>

              <div
                className={`inline-description`}
                style={{
                  fontSize: settings?.mainLayout.subHeaderFontSize + "px",
                  fontWeight: settings?.mainLayout.subHeaderFontWeight,
                  color: settings?.mainLayout.subHeaderTextColor,
                }}
                tabIndex={0}
                role="contentinfo"
                aria-label={settings?.mainLayout.subHeaderText || ""}
              >
                {settings?.mainLayout.subHeaderText || ""}
              </div>

              <EmailAutomaticTermsBanner
                campaignGeneralOptions={campaign?.campaignGeneralOptions}
                cookieOptions={campaign?.cookieOptions}
                text={headerEmailAutomaticTermsText}
                email={resolvedEmailForBanner}
                icon={settings?.dealLayout?.iconInfo?.value}
                onEmailChange={setPreEmail}
                mobile={device === LayoutDevices.mobile}
              />

              {settings?.filters?.filters && campaign && (
                <TemplateFilters
                  campaign={campaign}
                  settings={settings}
                  setFilteredList={handleFilteredListChange}
                />
              )}

              {settings &&
                campaign &&
                normalizeSectionSettings(settings.sections).topMenu.enabled && (
                  <PreviewTopMenu
                    settings={settings}
                    campaign={campaign}
                    offers={campaign.offers}
                    hasFilters={!!settings?.filters?.filters}
                  />
                )}

              {displayOnly &&
                campaign?.campaignGeneralOptions?.showAddToOrder && (
                  <PreCountAndSelectAll
                    offerLength={
                      offerFilteredList.filter((o) => !o.tagBlocked?.isBlocked)
                        .length
                    }
                    countText={settings?.otherSettings?.counterText}
                    selectText={settings?.otherSettings?.selectedText}
                    showCheckboxSelect={
                      settings?.otherSettings?.showCheckboxSelect || false
                    }
                    showSelectAll={
                      settings?.otherSettings?.showSelectAll || false
                    }
                    showCount={settings?.otherSettings?.showCounter || false}
                    noOfSelected={
                      offerFilteredList.filter(
                        (o) =>
                          selectedOffers.some(
                            (id) => String(id) === String(o.id)
                          ) && !o.tagBlocked?.isBlocked
                      ).length
                    }
                    handleSelectAll={handleSelectAll}
                    highlightColor={settings?.mainLayout?.highlightColor}
                    borderColor={settings?.dealLayout?.borderColor}
                  />
                )}

              <div id="inline-grid">
                {campaign && settings && (
                  <PreviewSections
                    settings={settings}
                    offers={offerFilteredList}
                    isMobile={device === LayoutDevices.mobile}
                    actions={{
                      sessionId,
                      displayOnly,
                      campaign,
                      campaignId: campaign.id,
                      placementId,
                      placementName,
                      settingsTemplate,
                      layoutId,
                      preEmail: preEmail || email,
                      setPreEmail: persistDynamicEmail,
                      openSingle,
                      onAdd: handleAdd,
                      canAutoSendEmailFromLabel,
                      canAutoSendEmailFromMainCTA,
                      onAutoSendEmailFromLabel: handleAutoSendFromOfferLabel,
                      autoSendingOfferId,
                      isSeen,
                    }}
                  />
                )}

                <div className="inline-footer">
                  {displayOnly ? (
                    <>
                      {campaign?.campaignGeneralOptions
                        .emailCaptureOnOffers && (
                          <>
                            <div role="img" aria-label="Information">
                              {!settings?.dealLayout?.icons ||
                                settings?.dealLayout?.icons ===
                                TemplateIcons.default ? (
                                <TemplateIcon
                                  icon={
                                    settings?.dealLayout.iconInfo?.value ||
                                    ICONS_DEFAULT_VALUES.iconInfo
                                  }
                                  color={
                                    settings?.mainLayout.textColor || "#000000"
                                  }
                                />
                              ) : (
                                <svg
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M12 0C5.37 0 0 5.37 0 12C0 18.63 5.37 24 12 24C18.63 24 24 18.63 24 12C24 5.37 18.63 0 12 0ZM13.5 18.5C13.5 19.33 12.83 20 12 20C11.17 20 10.5 19.33 10.5 18.5V10.5C10.5 9.67 11.17 9 12 9C12.83 9 13.5 9.67 13.5 10.5V18.5ZM12 7C11.17 7 10.5 6.33 10.5 5.5C10.5 4.67 11.17 4 12 4C12.83 4 13.5 4.67 13.5 5.5C13.5 6.33 12.83 7 12 7Z"
                                    fill={settings?.mainLayout.textColor}
                                  />
                                </svg>
                              )}
                            </div>
                            <FooterText
                              displayOnly={displayOnly}
                              footerText={settings?.mainLayout.footerText}
                              hasTooltip={false}
                            />
                          </>
                        )}
                    </>
                  ) : (
                    <>
                      <div role="img" aria-label="Information">
                        {!settings?.dealLayout?.icons ||
                          settings?.dealLayout?.icons ===
                          TemplateIcons.default ? (
                          <TemplateIcon
                            icon={
                              settings?.dealLayout.iconEmail?.value ||
                              ICONS_DEFAULT_VALUES.iconEmail
                            }
                            color={settings?.mainLayout.textColor || "#000000"}
                          />
                        ) : (
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M12 0C5.37 0 0 5.37 0 12C0 18.63 5.37 24 12 24C18.63 24 24 18.63 24 12C24 5.37 18.63 0 12 0ZM13.5 18.5C13.5 19.33 12.83 20 12 20C11.17 20 10.5 19.33 10.5 18.5V10.5C10.5 9.67 11.17 9 12 9C12.83 9 13.5 9.67 13.5 10.5V18.5ZM12 7C11.17 7 10.5 6.33 10.5 5.5C10.5 4.67 11.17 4 12 4C12.83 4 13.5 4.67 13.5 5.5C13.5 6.33 12.83 7 12 7Z"
                              fill={settings?.mainLayout.textColor}
                            />
                          </svg>
                        )}
                      </div>
                      <FooterText
                        displayOnly={displayOnly}
                        footerText={settings?.mainLayout.footerText}
                        hasTooltip={false}
                      />
                    </>
                  )}
                </div>
                {settings?.mainLayout.footerHTML && (
                  <div
                    className="inline-footer-html"
                    dangerouslySetInnerHTML={{
                      __html: settings?.mainLayout.footerHTML,
                    }}
                  ></div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PreviewDynamic;
